// Use Function constructor to bypass Turbopack's static analysis of dynamic imports
// This prevents the "expression is too dynamic" error
// eslint-disable-next-line @typescript-eslint/no-implied-eval
const dynamicImport = new Function('modulePath', 'return import(modulePath)');

// Type definitions
interface FFmpegModule {
  FFmpeg: new () => FFmpegInstance;
}

interface FFmpegUtilModule {
  fetchFile: (file: File | Blob | string) => Promise<Uint8Array>;
  toBlobURL: (url: string, mimeType: string) => Promise<string>;
}

interface FFmpegInstance {
  load: (config: { coreURL: string; wasmURL: string }) => Promise<void>;
  on: (event: string, callback: (data: { progress: number }) => void) => void;
  off: (event: string, callback: (data: { progress: number }) => void) => void;
  writeFile: (name: string, data: Uint8Array) => Promise<void>;
  readFile: (name: string) => Promise<Uint8Array>;
  deleteFile: (name: string) => Promise<void>;
  exec: (args: string[]) => Promise<void>;
}

// Singleton FFmpeg instance
let ffmpegInstance: FFmpegInstance | null = null;
let isFFmpegLoaded = false;

/**
 * Initialize FFmpeg instance (singleton pattern)
 */
export async function getFFmpeg(): Promise<FFmpegInstance> {
  if (ffmpegInstance && isFFmpegLoaded) {
    return ffmpegInstance;
  }

  // Use dynamic import via Function constructor to bypass Turbopack static analysis
  const ffmpegModule = await dynamicImport('@ffmpeg/ffmpeg') as FFmpegModule;
  const { FFmpeg } = ffmpegModule;
  const ffmpeg = new FFmpeg();
  
  // Load FFmpeg core and wasm files
  const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/esm';
  
  try {
    // Dynamically import toBlobURL
    const utilModule = await dynamicImport('@ffmpeg/util') as FFmpegUtilModule;
    const { toBlobURL } = utilModule;
    
    await ffmpeg.load({
      coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
      wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
    });
    
    ffmpegInstance = ffmpeg;
    isFFmpegLoaded = true;
    
    return ffmpeg;
  } catch (error) {
    console.error('Failed to load FFmpeg:', error);
    throw new Error('Failed to initialize FFmpeg. Please refresh the page and try again.');
  }
}

/**
 * Fetch file for FFmpeg (async wrapper for dynamic import)
 */
export async function fetchFile(file: File | Blob | string): Promise<Uint8Array> {
  const utilModule = await dynamicImport('@ffmpeg/util') as FFmpegUtilModule;
  return utilModule.fetchFile(file);
}

/**
 * Convert URL to blob URL for FFmpeg (async wrapper for dynamic import)
 */
export async function toBlobURL(url: string, mimeType: string): Promise<string> {
  const utilModule = await dynamicImport('@ffmpeg/util') as FFmpegUtilModule;
  return utilModule.toBlobURL(url, mimeType);
}

/**
 * Get video duration in seconds
 */
export async function getVideoDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    
    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(video.src);
      resolve(video.duration);
    };
    
    video.onerror = () => {
      window.URL.revokeObjectURL(video.src);
      reject(new Error('Failed to load video metadata'));
    };
    
    video.src = URL.createObjectURL(file);
  });
}

/**
 * Get video dimensions
 */
export async function getVideoDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    
    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(video.src);
      resolve({
        width: video.videoWidth,
        height: video.videoHeight,
      });
    };
    
    video.onerror = () => {
      window.URL.revokeObjectURL(video.src);
      reject(new Error('Failed to load video metadata'));
    };
    
    video.src = URL.createObjectURL(file);
  });
}

/**
 * Helper to convert FFmpeg output to File
 */
export function ffmpegOutputToFile(
  data: Uint8Array | ArrayBuffer | string,
  fileName: string,
  mimeType: string = 'video/mp4'
): File {
  let uint8Array: Uint8Array;
  
  if (data instanceof Uint8Array) {
    const buffer = data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength) as ArrayBuffer;
    uint8Array = new Uint8Array(buffer);
  } else if (typeof data === 'string') {
    const encoder = new TextEncoder();
    uint8Array = encoder.encode(data);
  } else {
    uint8Array = new Uint8Array(data);
  }
  
  const blob = new Blob([uint8Array as BlobPart], { type: mimeType });
  
  return new File([blob], fileName, {
    type: mimeType,
    lastModified: Date.now(),
  });
}

